import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Check, Loader, AlertCircle, ExternalLink, DollarSign } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Use relative URLs for same-origin requests (no CORS)
const API_URL = '';

// eslint-disable-next-line no-unused-vars
const RevShareSettings = ({ user, onUserUpdate }) => {
  const { toast } = useToast();
  const [accountStatus, setAccountStatus] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  useEffect(() => {
    checkAccountStatus();
  }, []);

  const checkAccountStatus = async () => {
    setIsCheckingStatus(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_URL}/api/monetization/stripe/account-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[STRIPE] Account status:', response.data);
      setAccountStatus(response.data);
    } catch (error) {
      console.error('[STRIPE] Error checking account status:', error);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleConnectStripe = async () => {
    setIsConnecting(true);

    try {
      const token = localStorage.getItem('access_token');
      console.log('[STRIPE] Calling create-account-link...');
      
      const response = await axios.post(
        `${API_URL}/api/monetization/stripe/create-account-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('[STRIPE] Response:', response.data);

      // Simple success check
      if (response.data?.success && response.data?.url) {
        // DIRECT redirect to Stripe - no SPA routing
        console.log('[STRIPE] Redirecting to:', response.data.url);
        window.location.href = response.data.url;
      } else {
        // Show error
        const errorMsg = response.data?.error || 'Failed to create Stripe link';
        console.error('[STRIPE] Error:', errorMsg);
        
        toast({
          title: 'Stripe Connect Error',
          description: errorMsg,
          variant: 'destructive'
        });
        setIsConnecting(false);
      }
    } catch (error) {
      console.error('[STRIPE] Exception:', error);
      console.error('[STRIPE] Response data:', error.response?.data);
      
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to connect to Stripe';
      
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive'
      });
      setIsConnecting(false);
    }
  };

  if (isCheckingStatus) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-600">Checking Stripe status...</span>
        </div>
      </Card>
    );
  }

  const isConnected = accountStatus?.connected && accountStatus?.details_submitted;

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-black mb-2">Free Plan (Rev-Share)</h3>
            <p className="text-gray-700">$0/month - Tips via Stripe, 10% platform fee</p>
          </div>
          <div className="bg-black p-3 rounded-lg">
            <DollarSign className="h-6 w-6 text-[#FFCC00]" />
          </div>
        </div>

        {!isConnected ? (
          <>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <p className="font-semibold mb-2">Connect your Stripe account to receive tips:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Accept tips via credit card, Apple Pay, Google Pay</li>
                    <li>You receive 90% of every tip</li>
                    <li>Mic Check Millionaire automatically takes 10%</li>
                    <li>Example: $20 tip → You get $18, MCM gets $2</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              onClick={handleConnectStripe}
              disabled={isConnecting}
              className="w-full bg-[#635BFF] hover:bg-[#5148E5] text-white py-6 text-lg"
              size="lg"
            >
              {isConnecting ? (
                <>
                  <Loader className="h-5 w-5 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="h-5 w-5 mr-2" />
                  Connect with Stripe
                </>
              )}
            </Button>

            {accountStatus?.connected && !accountStatus?.details_submitted && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                  <p className="text-sm text-yellow-800 font-medium">
                    You started connecting Stripe but didn&apos;t complete the setup. Click &quot;Connect with Stripe&quot; again to continue.
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 font-semibold">Stripe Connected</span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                You can now receive tips via credit/debit card and digital wallets
              </p>
            </div>

            {/* Revenue Split Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Revenue Split</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex justify-between">
                  <span>You receive:</span>
                  <span className="font-bold">90%</span>
                </div>
                <div className="flex justify-between">
                  <span>Mic Check Millionaire:</span>
                  <span className="font-bold">10%</span>
                </div>
                <div className="pt-2 border-t border-blue-300 mt-2">
                  <p className="italic">
                    Example: $50 tip = $45 to you, $5 to platform
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default RevShareSettings;
